import { useMemo } from 'react';

import { store } from 'Models/Store';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { getEffectiveCharacteristicIds } from 'Modules/Cargo';
import { MeasurementFilterContext } from 'Util/_HumanWritten/MeasurementRestrictions';
import { VehicleBookingWizardData } from '../../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../../BookingWizardDataStore';
import { useSubjectUserDisplayNameId } from '../../BookingDetails/useSubjectUserDisplayNameId';

export interface MeasurementFilterContexts {
	/** For the vehicle's own length / weight pickers. */
	vehicle: MeasurementFilterContext;
	/** For the trailer's, using the tow-on type's characteristics instead. */
	trailer: MeasurementFilterContext;
}

/**
 * Assembles what the measurement filter needs to know about the booking in progress: who is driving,
 * what the vehicle (or trailer) is, and which customer it is for.
 *
 * All three come from state the wizard already holds — the driver flag the passenger counters set,
 * the characteristics map in the clientside-config bundle, and the same customer-type lookup that
 * gates user-type-restricted dynamic booking fields.
 */
export function useMeasurementFilterContext(): MeasurementFilterContexts {
	const { wizardData } = useBookingWizardDataStore();
	const userDisplayNameId = useSubjectUserDisplayNameId();

	const inner = wizardData?.innerWizardData as VehicleBookingWizardData | undefined;
	const cargoTypeId = inner?.cargoTypeId ?? '';
	const trailerTypeId = inner?.trailerTypeId ?? '';

	// The driver flag lives on individual PassengerDetail rows, so the dependency has to be the
	// tickets themselves, not a count — moving the driver between two adults changes neither length.
	const driverPassengerTypeId = passengerTypeStore.getDriverPassengerTypeId(inner);

	const vehicleCharacteristicIds = useMemo(
		() =>
			getEffectiveCharacteristicIds(
				store.cargoTypeCharacteristicsMap?.[cargoTypeId] ?? [],
				inner?.cargoCharacteristics,
				inner?.removedInheritedCharacteristics,
			),
		[cargoTypeId, inner?.cargoCharacteristics, inner?.removedInheritedCharacteristics],
	);

	const trailerCharacteristicIds = useMemo(
		() =>
			getEffectiveCharacteristicIds(
				store.towOnTypeCharacteristicsMap?.[trailerTypeId] ?? [],
				inner?.trailerCargoCharacteristics,
				// Removals are recorded for the vehicle only; a trailer has no inherited-removal UI.
				[],
			),
		[trailerTypeId, inner?.trailerCargoCharacteristics],
	);

	return {
		vehicle: {
			driverPassengerTypeId,
			cargoCharacteristicIds: vehicleCharacteristicIds,
			userDisplayNameId,
		},
		trailer: {
			driverPassengerTypeId,
			cargoCharacteristicIds: trailerCharacteristicIds,
			userDisplayNameId,
		},
	};
}
